<script setup lang="ts">
import type { Broker, BrokerSimilar } from '~~/types/Broker'

// Component-referentie i.p.v. de string 'NuxtLink': een string in
// <component :is> resolvet alleen globaal geregistreerde componenten en
// rendert anders een dood <nuxtlink> custom element
const NuxtLink = resolveComponent('NuxtLink')

const props = defineProps<{
  broker: Broker
}>()

type ComparisonRow = {
  id: string
  broker_name: string
  broker_slug: string
  url: string
  total_sales: number
  avg_price: string
  avg_price_raw: number
  avg_days_on_market: number
  shared_cities: number
  rating: number
  total_reviews: number
  isCurrentBroker: boolean
}

const tableData = computed<ComparisonRow[]>(() => {
  if (!props.broker.similar_brokers?.length) return []

  const currentBrokerRow: ComparisonRow = {
    id: 'current',
    broker_name: props.broker.broker_name,
    broker_slug: props.broker.broker_slug,
    url: props.broker.url,
    total_sales: props.broker.houses_sold_last_12_months,
    avg_price: props.broker.avg_house_price,
    avg_price_raw: props.broker.avg_house_price_raw,
    avg_days_on_market: props.broker.avg_days_on_market,
    shared_cities: 0,
    rating: props.broker.rating,
    total_reviews: props.broker.total_reviews,
    isCurrentBroker: true,
  }

  const similarRows: ComparisonRow[] = props.broker.similar_brokers.map((similar: BrokerSimilar) => ({
    id: similar.broker_slug,
    broker_name: similar.broker_name,
    broker_slug: similar.broker_slug,
    url: similar.url,
    total_sales: similar.total_sales,
    avg_price: similar.avg_price,
    avg_price_raw: similar.avg_price_raw,
    avg_days_on_market: similar.avg_days_on_market,
    shared_cities: similar.shared_cities,
    rating: similar.rating,
    total_reviews: similar.total_reviews,
    isCurrentBroker: false,
  }))

  return [currentBrokerRow, ...similarRows]
})

const getDaysDiff = (days: number) => {
  const diff = days - props.broker.avg_days_on_market
  if (diff === 0) return null
  return diff
}

const hasSimilarBrokers = computed(() => props.broker.similar_brokers?.length > 0)
</script>

<template>
  <div
    v-if="hasSimilarBrokers"
    class="my-16"
  >
    <h2 class="text-2xl sm:text-3xl font-bold tracking-[-0.015em] text-highlighted text-balance mb-2">
      Vergelijk met andere makelaars
    </h2>
    <p class="text-muted text-pretty mb-8">
      Bekijk hoe {{ broker.broker_name }} presteert ten opzichte van andere makelaars in {{ broker.city_name }}.
    </p>

    <div class="rounded-xl border border-default overflow-hidden">
      <!-- Table header — desktop only -->
      <div class="hidden sm:grid grid-cols-12 gap-2 px-5 py-2.5 bg-muted border-b border-default text-xs font-medium text-muted">
        <div class="col-span-4">
          Makelaar
        </div>
        <div class="col-span-2 text-right">
          Verkocht
        </div>
        <div class="col-span-2 text-right">
          Gem. prijs
        </div>
        <div class="col-span-2 text-right">
          Verkooptijd
        </div>
        <div class="col-span-2 text-right">
          Reviews
        </div>
      </div>

      <!-- Rows -->
      <div class="divide-y divide-default">
        <component
          :is="row.isCurrentBroker ? 'div' : NuxtLink"
          v-for="row in tableData"
          :key="row.id"
          :to="row.isCurrentBroker ? undefined : row.url"
          class="grid grid-cols-2 sm:grid-cols-12 gap-x-2 gap-y-1 sm:gap-2 px-4 sm:px-5 py-3.5 items-center transition-colors duration-150"
          :class="[
            row.isCurrentBroker ? 'bg-muted' : 'hover:bg-muted cursor-pointer',
          ]"
        >
          <!-- Naam -->
          <div class="col-span-2 sm:col-span-4 min-w-0 mb-1 sm:mb-0">
            <div class="flex items-center gap-2">
              <NuxtImg
                :src="`/img/makelaars/${row.broker_slug}.webp`"
                :alt="row.broker_name"
                class="size-8 object-contain rounded-lg shrink-0"
                @error="(e: any) => { if (e?.target) (e.target as HTMLImageElement).src = '/img/makelaars/placeholder.webp' }"
              />
              <span
                class="font-semibold text-sm truncate"
                :class="row.isCurrentBroker ? 'text-highlighted' : 'text-primary'"
              >
                {{ row.broker_name }}
              </span>
              <UBadge
                v-if="row.isCurrentBroker"
                color="primary"
                variant="subtle"
                size="xs"
                class="shrink-0"
              >
                Huidig
              </UBadge>
            </div>
            <p
              v-if="!row.isCurrentBroker && row.shared_cities > 0"
              class="text-xs text-dimmed"
            >
              {{ row.shared_cities }} gedeelde werkgebieden
            </p>
          </div>

          <!-- Verkocht -->
          <div class="sm:col-span-2 text-right">
            <p class="text-xs text-dimmed sm:hidden">
              Verkocht
            </p>
            <p class="text-sm font-semibold text-highlighted tabular-nums">
              {{ row.total_sales }}
            </p>
          </div>

          <!-- Gem. prijs -->
          <div class="sm:col-span-2 text-right">
            <p class="text-xs text-dimmed sm:hidden">
              Gem. prijs
            </p>
            <p class="text-sm font-semibold text-highlighted tabular-nums">
              {{ row.avg_price }}
            </p>
          </div>

          <!-- Verkooptijd -->
          <div class="sm:col-span-2 text-right">
            <p class="text-xs text-dimmed sm:hidden">
              Verkooptijd
            </p>
            <p class="text-sm tabular-nums text-highlighted">
              {{ row.avg_days_on_market }} dgn
              <span
                v-if="!row.isCurrentBroker && getDaysDiff(row.avg_days_on_market) != null"
                class="text-xs font-normal"
                :class="getDaysDiff(row.avg_days_on_market)! < 0 ? 'text-success' : 'text-warning'"
              >
                ({{ getDaysDiff(row.avg_days_on_market)! > 0 ? '+' : '' }}{{ getDaysDiff(row.avg_days_on_market) }})
              </span>
            </p>
          </div>

          <!-- Reviews -->
          <div class="sm:col-span-2 text-right">
            <p class="text-xs text-dimmed sm:hidden">
              Reviews
            </p>
            <div
              v-if="row.total_reviews > 0"
              class="flex items-center justify-end gap-1"
            >
              <UIcon
                name="i-lucide-star"
                class="text-warning size-3.5"
              />
              <span class="text-sm font-semibold text-highlighted tabular-nums">{{ row.rating.toFixed(1) }}</span>
              <span class="text-xs text-muted tabular-nums">({{ row.total_reviews }})</span>
            </div>
            <span
              v-else
              class="text-sm text-dimmed"
            >–</span>
          </div>
        </component>
      </div>
    </div>

    <!-- CTA -->
    <div class="mt-6 text-center">
      <UButton
        color="neutral"
        variant="outline"
        trailing-icon="i-lucide-arrow-right"
        :to="broker.location_url"
      >
        Bekijk alle makelaars in {{ broker.city_name }}
      </UButton>
    </div>
  </div>
</template>
